import pillBottle from "assets/images/pill-bottle.png";
import {useState} from "react";
import {useNavigate, useLocation, useSearchParams} from "react-router-dom";
import {common as stringsCommon, resetPassword as strings} from "strings";

import Button from "components/Button";
import MobileNavigationHeader from "components/MobileNavigationHeader";
import PageTitle from "components/PageTitle";
import StyledModal from "components/StyledModal";
import TextInput from "components/TextInput";

import {useResetPassword} from "liberty-core/api/react-query/user";

import {useAlert} from "lib/alert";

import styles from "./ResetPasswordPage.module.css";

const errorFieldNames = {
  email: "Email",
  verificationCode: "VerificationCode",
  newPassword: "NewPassword",
};

type LocationState = {
  email?: string;
};

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const locationState = location.state as LocationState;
  const [email, setEmail] = useState(locationState?.email ?? "");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{[field: string]: string[]}>(
    {},
  );

  const {mutateAsync: resetPassword, isLoading} = useResetPassword();
  const alert = useAlert();

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (e) {
      e.preventDefault();
    }

    if (isLoading || !email || !resetCode || !newPassword) {
      return;
    }

    resetPassword({email, verificationCode: resetCode, newPassword})
      .then(() => {
        setShowSuccessModal(true);
      })
      .catch((err) => {
        if (err?.response?.status === 400 && err?.response?.data?.errors) {
          setFieldErrors(err.response.data.errors);
        }

        alert({
          title: stringsCommon.whoops,
          message:
            err?.response?.data?.generic?.join("\n") ??
            stringsCommon.somethingWentWrong,
        });
      });
  };

  return (
    <div className={styles.container}>
      <div className={styles.resetPasswordContainer}>
        <MobileNavigationHeader
          rightLink={{label: stringsCommon.login, to: "/login"}}
        />
        <form className={styles.resetInfo} onSubmit={onSubmit}>
          <div className={styles.resetPasswordLabel}>
            {strings.resetPassword}
          </div>
          <div className={styles.resetInstructions}>{strings.description}</div>
          <TextInput
            className={styles.resetInput}
            label={stringsCommon.email}
            type="email"
            autoComplete="email"
            value={email}
            onChangeText={setEmail}
            required={true}
            errorText={fieldErrors[errorFieldNames.email]?.join("\n")}
          />
          <TextInput
            className={styles.resetInput}
            label={strings.resetCode}
            type="text"
            autoComplete="one-time-code"
            value={resetCode}
            onChangeText={setResetCode}
            required={true}
            errorText={fieldErrors[errorFieldNames.verificationCode]?.join(
              "\n",
            )}
          />
          <TextInput
            className={styles.resetInput}
            label={strings.newPassword}
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChangeText={setNewPassword}
            required={true}
            errorText={fieldErrors[errorFieldNames.newPassword]?.join("\n")}
          />
          <Button
            className={styles.resetPasswordButton}
            disabled={!email || !resetCode || !newPassword}
            variant="filled"
            loading={isLoading}
            type="submit"
          >
            {strings.resetPassword}
          </Button>
        </form>
      </div>
      <div className={styles.pillBottleContainer}>
        <img className={styles.pillBottleImage} src={pillBottle} alt="" />
      </div>
      <StyledModal
        visible={showSuccessModal}
        className={styles.modal}
        overlayClassName={styles.overlay}
        onRequestClose={() =>
          navigate(`/login?${searchParams.toString()}`, {replace: true})
        }
      >
        <div className={styles.modalContent}>
          <PageTitle className={styles.successText}>
            {stringsCommon.success}
          </PageTitle>
          <div className={styles.descriptiveText}>{strings.successBody}</div>
          <Button
            className={styles.returnButton}
            to={`/login?${searchParams.toString()}`}
            replace={true}
          >
            {strings.returnToLogin}
          </Button>
        </div>
      </StyledModal>
    </div>
  );
}
